import 'photoswipe/dist/photoswipe.css';

import { Alerts, Button, ChoiceChip, Text, Textarea } from '@components/ui';
import { useSaveRatingCriteriaMutation } from '@lib/api/rating';
import { uploadFile,type UploadFolder } from '@lib/api/uploads';
import { logError } from '@lib/logError';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Buffer } from 'buffer';
import { type ChangeEvent, type Ref, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploading, { type ImageListType } from 'react-images-uploading';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { toast } from 'react-toastify';

import type {
  CriteriaApiError,
  CriteriaImage,
  DamageItem,
  RatingCriteria,
  RatingOption,
} from './criteriaOptionTypes';
const browserWindow = window as Window & { Buffer?: typeof Buffer };
browserWindow.Buffer = browserWindow.Buffer || Buffer;

interface CriteriaOptionProps {
  ratingCriteriaOptions: RatingOption[];
  damages?: DamageItem[];
  ratingCriteria: RatingCriteria;
  ratingCriteriaId: string | number;
  jobId: string | number;
  handleR8tingCategoryListUpdates: () => void;
  criteriaListTitle: string;
}

const needsDetails = (value: number | string | undefined): boolean => {
  if (value == 'NO') {
    return true;
  }
  if (value === undefined) {
    return false;
  }
  return Number(value) < 8;
};

const CriteriaOption = ({
  ratingCriteriaOptions,
  damages,
  ratingCriteria,
  ratingCriteriaId,
  jobId,
  handleR8tingCategoryListUpdates,
  criteriaListTitle,
}: CriteriaOptionProps): JSX.Element => {
  const { t } = useTranslation();
  const [r8tingCriteriaDamageIds, setR8tingCriteriaDamageIds] = useState<
    Array<string | number>
  >([]);
  const [r8tingCriteriaOptionId, setR8tingCriteriaOptionId] = useState<
    string | number | null
  >(null);
  const [r8tingCriteriaOptionValue, setR8tingCriteriaOptionValue] = useState<
    number | string | undefined
  >();
  const [massage, setMassage] = useState('');
  const [images, setImages] = useState<CriteriaImage[]>([]);
  const [loading, setLoading] = useState(false);
  const saveCriteria = useSaveRatingCriteriaMutation();
  const onChange = (imageList: ImageListType): void => {
    setImages(imageList as CriteriaImage[]);
  };
  const handleimageDelete = (
    removeImage: (index: number) => void,
    index: number
  ): void => {
    removeImage(index);
  };
  const handleDeleteOrUpdate = (
    removeImage: (index: number) => void,
    index: number
  ): void => {
    Alerts.deleteImageAlert(handleimageDelete, removeImage, index);
  };
  useEffect(() => {
    if (ratingCriteria.ratingCriteriaUser != null) {
      setR8tingCriteriaDamageIds(
        ratingCriteria.ratingCriteriaUser.damageIds ?? []
      );
      setR8tingCriteriaOptionId(
        ratingCriteria.ratingCriteriaUser.ratingCriteriaOptionId ?? null
      );
      setR8tingCriteriaOptionValue(
        ratingCriteria.ratingCriteriaUser.ratingValue
      );
      setImages(ratingCriteria.ratingCriteriaUser.images ?? []);
      setMassage(ratingCriteria.ratingCriteriaUser.criteriaInfo ?? '');
    }
  }, [ratingCriteria]);
  const handler8tingCriteriaDamageTab = (id: string | number): void => {
    const damageIds = [...r8tingCriteriaDamageIds];
    const existingDamageIdIndex = damageIds.indexOf(id);
    if (existingDamageIdIndex > -1) {
      damageIds.splice(existingDamageIdIndex, 1);
    } else {
      damageIds.push(id);
    }
    setR8tingCriteriaDamageIds(damageIds);
  };

  const handler8tingCriteriaOptionTab = (
    id: string | number,
    value: number | string | undefined
  ): void => {
    setR8tingCriteriaOptionValue(value);
    if (r8tingCriteriaOptionId == id) {
      setR8tingCriteriaOptionId(null);
    } else {
      setR8tingCriteriaOptionId(id);
    }
  };
  const handleRatingCriteriaResult = async (): Promise<void> => {
    if (needsDetails(r8tingCriteriaOptionValue)) {
      if (!massage) {
        toast.error('Message is required');
        return;
      }
      if (images.length < 1) {
        toast.error('Atleast one image is required');
        return;
      }
    }

    setLoading(true);
    const latestImagesUrls = await handleUploadImage();
    saveCriteria
      .mutateAsync({
        jobId,
        ratingCriteriaId,
        ratingCriteriaOptionId: r8tingCriteriaOptionId,
        damageIds: r8tingCriteriaDamageIds,
        criteriaInfo: massage,
        images: latestImagesUrls,
      })
      .then((res) => {
        setLoading(false);
        toast.success(`${criteriaListTitle} ${res.data.message}`);
        handleR8tingCategoryListUpdates();
      })
      .catch((error: CriteriaApiError) => {
        setLoading(false);
        logError('criteria option', error);
        const errorMassage = error.response.data.message;
        toast.error(errorMassage);
      });
  };

  const handleUploadImage = async (): Promise<string[] | undefined> => {
    // Same layout the API and mobile app use for criteria media
    const folder: UploadFolder = r8tingCriteriaOptionId
      ? `job-ratings-media/${jobId}/${ratingCriteriaId}/${r8tingCriteriaOptionId}`
      : `job-ratings-media/${jobId}/${ratingCriteriaId}`;

    try {
      const tempImagesArray: string[] = [];
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        if (!image) {
          continue;
        }
        if (image.file) {
          const uploadRes = await uploadFile(image.file, folder);
          tempImagesArray.push(uploadRes.location);
        } else if (image.url) {
          tempImagesArray.push(image.url);
        }
      }
      return tempImagesArray;
    } catch (error: unknown) {
      setLoading(false);
      logError('criteria option', error);
      toast.error('Error in uploading files');
      return;
    }
  };

  const handleMassageChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setMassage(event.target.value);
  };

  return (
    <Box>
      <Box id="pills">
        <Text sx={{ fontSize: 14, fontWeight: 700 }}>{t('rating.damage')}</Text>
        <Box
          role="group"
          sx={{
            display: 'flex',
            cursor: 'pointer',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            p: 0,
            m: 0,
          }}
        >
          {damages &&
            damages.length > 0 &&
            damages.map((item) => {
              const selected = r8tingCriteriaDamageIds.includes(item.id);
              return (
                <ChoiceChip
                  key={item.id}
                  selected={selected}
                  onClick={() => {
                    handler8tingCriteriaDamageTab(item.id);
                  }}
                  sx={{ ml: '10px', mt: '10px', fontSize: 12 }}
                >
                  {item.title}
                </ChoiceChip>
              );
            })}
        </Box>
      </Box>
      <Box sx={{ mt: 2.5 }}>
        <Box
          role="group"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            p: 0,
            m: 0,
          }}
        >
          {ratingCriteriaOptions.length > 0 &&
            ratingCriteriaOptions.map((item) => {
              const selected = r8tingCriteriaOptionId == item.id;
              return (
                <ChoiceChip
                  key={item.id}
                  selected={selected}
                  onClick={() => {
                    handler8tingCriteriaOptionTab(item.id, item.ratingValue);
                  }}
                  sx={{ ml: '10px', borderRadius: '10px', px: '15px' }}
                >
                  {item.ratingValue}
                </ChoiceChip>
              );
            })}
        </Box>
        {ratingCriteriaOptions.length > 0 &&
          ratingCriteriaOptions
            .filter((item) => item.id == r8tingCriteriaOptionId)
            .map((item) => (
              <Text
                key={item.id}
                sx={{
                  mt: 1,
                  fontSize: 14,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                {item.ratingTitle}
              </Text>
            ))}
      </Box>
      <Box
        component="hr"
        sx={{ border: 0, borderTop: 1, borderColor: 'divider' }}
      />
      <Box sx={{ mt: '20px' }}>
        <Text sx={{ fontSize: 14, fontWeight: 700 }}>
          {t('rating.uploadPhoto')}
        </Text>
        <ImageUploading
          multiple
          acceptType={['jpg', 'gif', 'png', 'jpeg', 'tif', 'tiff']}
          value={images as ImageListType}
          onChange={onChange}
          dataURLKey="url"
        >
          {({ imageList, onImageUpload, onImageRemove }) => (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              &nbsp;
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <Grid item xs={6} sm={4} md={3} lg={3}>
                  <Box
                    onClick={onImageUpload}
                    sx={{ height: 90, width: '100%', cursor: 'pointer' }}
                  >
                    <CloudUploadOutlinedIcon sx={{ fontSize: 48 }} />
                  </Box>
                </Grid>
                <Gallery>
                  {(imageList as CriteriaImage[]).map((image, index) => (
                    <Grid item xs={6} sm={4} md={3} lg={3} key={index}>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          height: 90,
                          width: '100%',
                          cursor: 'pointer',
                        }}
                      >
                        <Item
                          original={image.url}
                          thumbnail={image.url}
                          width="1024"
                          height="668"
                        >
                          {({ ref, open }) => (
                            <img
                              ref={ref as Ref<HTMLImageElement>}
                              onClick={open}
                              src={image.url}
                              alt=""
                              style={{
                                height: '120px',
                                width: '100%',
                                objectFit: 'cover',
                                borderRadius: 10,
                              }}
                            />
                          )}
                        </Item>
                        &nbsp;
                        <Box
                          component="span"
                          onClick={() =>
                            handleDeleteOrUpdate(onImageRemove, index)
                          }
                          sx={{
                            position: 'absolute',
                            right: '5px',
                            top: '2px',
                            zIndex: 3,
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <HighlightOffIcon
                            sx={{
                              fontSize: 18,
                              textAlign: 'center',
                              bgcolor: 'common.white',
                              borderRadius: '25px',
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Gallery>
              </Grid>
            </Box>
          )}
        </ImageUploading>
      </Box>
      <Box sx={{ mt: '45px', width: '100%', color: 'text.secondary' }}>
        <Text sx={{ fontSize: 14, fontWeight: 700 }}>
          {t('rating.message')}
        </Text>
        <Textarea
          required
          value={massage}
          placeholder={t('rating.notesPlaceholder')}
          onChange={handleMassageChange}
          minRows={3}
          sx={{ '& textarea': { resize: 'none' } }}
        />
      </Box>
      <Box
        sx={{
          mb: '25px',
          mt: '25px',
          display: 'flex',
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button
          variant="primary"
          disabled={!r8tingCriteriaOptionId}
          onClick={() => {
            void handleRatingCriteriaResult();
          }}
          sx={{
            height: 45,
            borderRadius: '0px 25px 0px 25px',
            color: 'common.white',
          }}
          loading={loading}
        >
          {' '}
          {t('common.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default CriteriaOption;
